/**
 * TeamStrength Hub - Auth Context & Provider
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from './api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      setIsLoading(false);
      return false;
    }
    
    try {
      const response = await authApi.getMe();
      setUser(response.data);
      
      // Load organizations
      const orgsResponse = await authApi.getOrganizations();
      setOrganizations(orgsResponse.data);
      
      // Set current org from localStorage or first org
      const savedOrgId = localStorage.getItem('current_org_id');
      const savedOrg = orgsResponse.data.find(o => o.org_id === savedOrgId);
      if (savedOrg) {
        setCurrentOrg(savedOrg);
      } else if (orgsResponse.data.length > 0) {
        setCurrentOrg(orgsResponse.data[0]);
        localStorage.setItem('current_org_id', orgsResponse.data[0].org_id);
      }
      
      // Check superadmin status
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : '');
        const adminRes = await fetch(`${backendUrl}/api/superadmin/check`, {
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const adminData = await adminRes.json();
        setIsSuperAdmin(adminData?.is_superadmin || false);
      } catch {
        setIsSuperAdmin(false);
      }
      
      return true;
    } catch (error) {
      localStorage.removeItem('session_token');
      setUser(null);
      setOrganizations([]);
      setCurrentOrg(null);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await authApi.login({ email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('session_token', access_token);
    setUser(userData);
    await checkAuth();
    return userData;
  };

  const signup = async (name, email, password) => {
    const response = await authApi.signup({ name, email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('session_token', access_token);
    setUser(userData);
    return userData;
  };

  const exchangeSession = async (sessionId) => {
    const response = await authApi.exchangeSession(sessionId);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('session_token', access_token);
    setUser(userData);
    await checkAuth();
    return userData;
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      // Ignore errors
    }
    localStorage.removeItem('session_token');
    localStorage.removeItem('current_org_id');
    setUser(null);
    setOrganizations([]);
    setCurrentOrg(null);
  };

  const switchOrg = (org) => {
    setCurrentOrg(org);
    localStorage.setItem('current_org_id', org.org_id);
  };

  const refreshOrgs = async () => {
    try {
      const orgsResponse = await authApi.getOrganizations();
      setOrganizations(orgsResponse.data);
      return orgsResponse.data;
    } catch {
      return [];
    }
  };

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    organizations,
    currentOrg,
    isSuperAdmin,
    login,
    signup,
    exchangeSession,
    logout,
    switchOrg,
    refreshOrgs,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
